// @flow

import * as React from 'react';
import { Box, useSystem } from 'react-system';
import { ButtonGroup, Button } from '@material-ui/core';
import { useTheme } from '../../../theme';
import { Filter } from '../../../controls/Filter';
import { useLocale } from '../../../hooks/locale';
import { createDescriptor } from '../../../controls/router';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql.js';
import { pageDescriptor, roomsFilterDescriptor } from './sharedDescriptors';

const combinedFilterDescriptor = createDescriptor(
  roomsFilterDescriptor,
  pageDescriptor,
);

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

// Material-ui ButtonGroup clones Button elements replacing all props with it's own
// here is the hack to allow button to have it's own variant and color
const WrapButton = ({ wvariant, wcolor, ...props }) => (
  <Button {...props} variant={wvariant} color={wcolor} />
);

export const RoomsFilter = (props: Props): React.Node => {
  const { t } = useLocale();
  const { text } = useTheme();
  const [
    roomsFilters,
    setRoomsFilters,
    flushRoomsFilters,
  ] = combinedFilterDescriptor.useState();
  const empty = roomsFilterDescriptor.useIsEmpty();
  const { media } = useSystem();
  const flush = () => {
    setRoomsFilters({ page: 0 });
    if (props.shouldFlush) {
      flushRoomsFilters();
    }
  };

  const [selectionIndex, setSelectionIndex] = React.useState(null);

  const dialogTitle = t('roomsFilterTitle');

  const gte = roomsFilters.numberOfRooms_gte;
  const lte = roomsFilters.numberOfRooms_lte;

  const checkboxes: Array<{|
    lte: number | null,
    gte: number,
    title: string,
    checked: boolean,
    insideSelectionRange: boolean,
  |}> = Array.from(Array(5).keys()).map(k => ({
    gte: k + 1,
    lte: k + 1.5,
    title: `${k + 1}`,
    checked:
      (gte != null || lte != null) &&
      (gte == null || gte <= k + 1) &&
      (lte == null || lte >= k + 1),
    insideSelectionRange: false,
  }));
  checkboxes.push({
    gte: 6,
    lte: null,
    title: '6+',
    checked: gte != null && gte <= 6 && lte == null,
    insideSelectionRange: false,
  });

  const firstCheckedIndex = checkboxes.findIndex(ch => ch.checked);
  const checkeds = checkboxes.filter(ch => ch.checked);

  let labelTitle = dialogTitle;

  // 6+ selected
  const isLastSelected = checkboxes[checkboxes.length - 1].checked;

  if (isLastSelected) {
    labelTitle = t('roomsFromSelected', { count: firstCheckedIndex + 1 });
  } else {
    if (checkeds.length === 1) {
      labelTitle = t('roomsSingleSelected', { count: firstCheckedIndex + 1 });
    }

    if (checkeds.length > 1) {
      const lastIndex = checkboxes.map(ch => ch.checked).lastIndexOf(true);
      labelTitle = t('roomsMultiSelected', {
        from: firstCheckedIndex + 1,
        to: lastIndex + 1,
      });
    }
  }

  if (selectionIndex != null && checkeds.length === 1) {
    for (
      let i = Math.min(firstCheckedIndex, selectionIndex);
      i <= Math.max(firstCheckedIndex, selectionIndex);
      i += 1
    ) {
      checkboxes[i].insideSelectionRange = true;
    }
  }

  const onClick = index => {
    const rangeStartOrEnd =
      firstCheckedIndex !== -1 && checkeds.length === 1
        ? firstCheckedIndex
        : index;

    const rangeStart = Math.min(rangeStartOrEnd, index);
    const rangeEnd = Math.max(rangeStartOrEnd, index);
    const newChecks = checkboxes.map(ch => ({ ...ch }));

    if (rangeEnd - rangeStart === 0) {
      if (checkeds.length <= 1) {
        newChecks[rangeStart].checked = !newChecks[rangeStart].checked;
      } else {
        newChecks.forEach(ch => {
          ch.checked = false;
        });
        newChecks[rangeStart].checked = true;
      }
    } else {
      for (let i = rangeStart; i <= rangeEnd; i += 1) {
        newChecks[i].checked = true;
      }
    }

    const checked = newChecks.filter(x => x.checked);

    setRoomsFilters({
      numberOfRooms_gte:
        checked.length === 0 ? null : Math.min(...checked.map(x => x.gte)),
      numberOfRooms_lte:
        checked.length === 0
          ? null
          : checked
              .map(x => x.lte)
              .reduce(
                (acc, cur) =>
                  acc == null || cur == null ? null : Math.max(acc, cur),
                1,
              ),
    });

    flush();
  };

  const inlineElement = (
    <>
      <Box my={3} css={text.subtitle1}>
        {dialogTitle}
      </Box>
      <ButtonGroup fullWidth={true}>
        <WrapButton
          css={[
            {
              padding: '8px 16px',
              fontWeight: 600,
              width: 'auto',
              flexShrink: 0,
            },
            empty
              ? { border: '1px solid transparent' }
              : { border: '1px solid rgba(0, 0, 0, 0.23)' },
          ]}
          onClick={() => {
            setRoomsFilters(roomsFilterDescriptor.defaults);
            flush();
          }}
          wvariant={empty ? 'contained' : 'outlined'}
          wcolor={empty ? 'primary' : 'inherit'}
        >
          {t('roomsButtonAny')}
        </WrapButton>

        {checkboxes.map((ch, index) => (
          <WrapButton
            key={index}
            onMouseEnter={() => {
              if (checkeds.length === 1) {
                setSelectionIndex(index);
              }
            }}
            onMouseLeave={() => setSelectionIndex(null)}
            css={[
              media({
                padding: ['8px 0', '8px 16px'],
                fontWeight: 600,
                borderLeftColor: 'rgba(0, 0, 0, 0.1)',
              }),
              ch.checked || ch.insideSelectionRange
                ? {
                    border: '1px solid transparent',
                  }
                : {
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                  },
            ]}
            onClick={() => {
              onClick(index);
            }}
            wvariant={
              ch.checked || ch.insideSelectionRange ? 'contained' : 'outlined'
            }
            wcolor={
              ch.checked || ch.insideSelectionRange ? 'primary' : 'inherit'
            }
          >
            {ch.title}
          </WrapButton>
        ))}
      </ButtonGroup>
    </>
  );

  return props.inline === true ? (
    inlineElement
  ) : (
    <Filter
      label={labelTitle}
      empty={empty}
      onReset={() => {
        setRoomsFilters(roomsFilterDescriptor.defaults);
        flush();
      }}
    >
      <Box p={3}>{inlineElement}</Box>
    </Filter>
  );
};
